import React, { useMemo, useRef, useState } from "react";
import { DiaSerie } from "../../../../services/v2/numeros.service";

const SERIES: { k: "enviados" | "recibidos"; t: string; clase: string }[] = [
	{ k: "enviados", t: "Enviados", clase: "s1" },
	{ k: "recibidos", t: "Recibidos", clase: "s2" },
];
const MESES = ["ene", "feb", "mar", "abr", "may", "jun", "jul", "ago", "sep", "oct", "nov", "dic"];
const fechaCorta = (d: string) => { const [, m, dd] = d.split("-"); return Number(dd) + " " + MESES[Number(m) - 1]; };
const fmt = (n: number) => n.toLocaleString("es-MX");

/**
 * 30 días del número: enviados y recibidos en WAPP (una sola escala). Al pasar el mouse, el día con todo su detalle
 * (leídos, fallidos y lo que Meta reporta como enviado y entregado). Con "Ver tabla" los mismos datos en filas.
 */
const GraficaNumero: React.FC<{ serie: DiaSerie[] }> = ({ serie }) => {
	const [hover, setHover] = useState<number | null>(null);
	const [tabla, setTabla] = useState<boolean>(false);
	const caja = useRef<SVGSVGElement>(null);
	const W = 1000, H = 230, PL = 44, PR = 12, PT = 12, PB = 26;
	const max = useMemo(() => {
		const m = Math.max(1, ...serie.map((d) => Math.max(d.enviados, d.recibidos)));
		const paso = Math.pow(10, Math.floor(Math.log10(m)));
		return Math.ceil(m / paso) * paso;
	}, [serie]);
	const x = (i: number) => PL + (i * (W - PL - PR)) / Math.max(1, serie.length - 1);
	const y = (v: number) => PT + (H - PT - PB) * (1 - v / max);
	const linea = (k: "enviados" | "recibidos") => serie.map((d, i) => (i ? "L" : "M") + x(i).toFixed(1) + " " + y(d[k]).toFixed(1)).join(" ");
	const mover = (e: React.MouseEvent<SVGSVGElement>) => {
		const r = caja.current?.getBoundingClientRect();
		if (!r) return;
		const px = ((e.clientX - r.left) / r.width) * W;
		const i = Math.round(((px - PL) / (W - PL - PR)) * (serie.length - 1));
		setHover(i >= 0 && i < serie.length ? i : null);
	};
	const d = hover !== null ? serie[hover] : null;
	const totales = serie.reduce((a, s) => ({ env: a.env + s.enviados, rec: a.rec + s.recibidos, lei: a.lei + s.leidos, fal: a.fal + s.fallidos }), { env: 0, rec: 0, lei: 0, fal: 0 });

	return (
		<div className="nu-grafica">
			<div className="nu-grafica-cab">
				<div className="nu-leyenda">
					{SERIES.map((s) => <span key={s.k}><i className={s.clase} />{s.t} <b>{fmt(s.k === "enviados" ? totales.env : totales.rec)}</b></span>)}
					<span className="tenue">Leídos <b>{fmt(totales.lei)}</b></span>
					<span className="tenue">Fallidos <b>{fmt(totales.fal)}</b></span>
				</div>
				<button className="nu-btn chico" onClick={() => setTabla(!tabla)}>{tabla ? "Ver gráfica" : "Ver tabla"}</button>
			</div>
			{tabla ? (
				<div className="nu-tabla-scroll">
					<table className="nu-tabla">
						<thead><tr><th>Día</th><th>Enviados</th><th>Recibidos</th><th>Leídos</th><th>Fallidos</th><th>Meta: enviados</th><th>Meta: entregados</th></tr></thead>
						<tbody>{[...serie].reverse().map((s) => (
							<tr key={s.dia}><td>{fechaCorta(s.dia)}</td><td>{fmt(s.enviados)}</td><td>{fmt(s.recibidos)}</td><td>{fmt(s.leidos)}</td><td>{fmt(s.fallidos)}</td>
								<td>{s.meta_enviados === null ? "—" : fmt(s.meta_enviados)}</td><td>{s.meta_entregados === null ? "—" : fmt(s.meta_entregados)}</td></tr>
						))}</tbody>
					</table>
				</div>
			) : (
				<div className="nu-grafica-lienzo">
					<svg ref={caja} viewBox={`0 0 ${W} ${H}`} role="img" aria-label="Mensajes enviados y recibidos en 30 días" onMouseMove={mover} onMouseLeave={() => setHover(null)}>
						{[0, 0.5, 1].map((f) => (
							<g key={f}>
								<line x1={PL} x2={W - PR} y1={y(max * f)} y2={y(max * f)} className="nu-rejilla" />
								<text x={PL - 6} y={y(max * f) + 4} className="nu-eje" textAnchor="end">{fmt(Math.round(max * f))}</text>
							</g>
						))}
						{serie.map((s, i) => (i % 7 === 0 || i === serie.length - 1) ? <text key={s.dia} x={x(i)} y={H - 6} className="nu-eje" textAnchor="middle">{fechaCorta(s.dia)}</text> : null)}
						{SERIES.map((s) => <path key={s.k} d={linea(s.k)} className={"nu-linea " + s.clase} />)}
						{hover !== null && d ? (
							<g>
								<line x1={x(hover)} x2={x(hover)} y1={PT} y2={H - PB} className="nu-cruz" />
								{SERIES.map((s) => <circle key={s.k} cx={x(hover)} cy={y(d[s.k])} r={4.5} className={"nu-punto " + s.clase} />)}
							</g>
						) : null}
					</svg>
					{hover !== null && d ? (
						<div className="nu-tip" style={{ left: `${(x(hover) / W) * 100}%` }}>
							<b>{fechaCorta(d.dia)}</b>
							<span><i className="s1" />Enviados {fmt(d.enviados)}</span>
							<span><i className="s2" />Recibidos {fmt(d.recibidos)}</span>
							<span>Leídos {fmt(d.leidos)} · Fallidos {fmt(d.fallidos)}</span>
							{d.meta_enviados !== null ? <span className="tenue">Meta: {fmt(d.meta_enviados)} enviados, {fmt(d.meta_entregados || 0)} entregados</span> : null}
						</div>
					) : null}
				</div>
			)}
		</div>
	);
};

export default GraficaNumero;
